import type { MultiSelectChrome } from '@/lib/transforms/types/multi-select-chrome'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { selectionChromeFrameForSelection } from '@/lib/shapes/selection-chrome-preview'

/**
 * Resolves which box the chrome frames: the manager's rigid group box when the group carries its own
 * rotation, otherwise a freshly computed frame for the selection. Shared so the SVG chrome and the
 * HTML badge overlay cannot disagree about where the frame is.
 *
 * **Why the manager's box is not simply preferred whenever it exists.** Both are visual-bounds
 * unions now — `computeMultiSelectChrome` → `unionCanvasAabbForShapes` → `rotatedVisualAabbOfShape`
 * on the manager's side, `selectionChromeFrameForSelection` → `selectionChromeUnionOfShapes` →
 * `rotatedVisualAabbOfShape` on the fresh-computation side — so for an *unrotated* group either one
 * gives the same, correct answer, and the fresh union is preferred: it comes straight from `selected`
 * (this render's live shapes), with nothing to go stale between the manager's own update and this
 * component's render.
 *
 * A ROTATED group is the one case a fresh recompute cannot serve: an axis-aligned union of rotated
 * visual AABBs cannot express a single rotated group frame, only the union's own axis-aligned
 * envelope. Only then does this fall back to the manager's box — the same one the live rotate
 * gesture is transforming, so it is guaranteed to already carry the correct oriented rect.
 *
 * **Deliberately presentation-only.** `multiSelectChromeRef` also feeds the resize/rotate gesture
 * math and the undo chrome snapshots, so `computeMultiSelectChrome` stays a `shape-manager`
 * concern rather than being inlined here — see its own consumers in `shape-manager/hooks/`,
 * `use-undo-redo.ts` and `multi-select-chrome-resize.ts`.
 */
export function resolveSelectionChromeFrame(
  selected: ShapeData[],
  multiSelectChrome?: MultiSelectChrome | null
) {
  if (selected.length > 1 && multiSelectChrome && multiSelectChrome.rotationDeg !== 0) {
    return {
      rect: {
        x: multiSelectChrome.pivotX - multiSelectChrome.width / 2,
        y: multiSelectChrome.pivotY - multiSelectChrome.height / 2,
        width: multiSelectChrome.width,
        height: multiSelectChrome.height,
      },
      pivot: { x: multiSelectChrome.pivotX, y: multiSelectChrome.pivotY },
      rotationDeg: multiSelectChrome.rotationDeg,
    }
  }

  return selectionChromeFrameForSelection(selected)
}
